import { getFileType } from '../../../api/structs/clip';
import { isRecord } from '../../../api/typeHelpers';

/** Where a carousel render output belongs (Go remotionrender slideOutputMeta). */
export interface SlideTag {
  /** 1-based slide number. */
  index: number;
  role: 'still' | 'video';
}

/** One tile of a mixed render: a slide's still, its clip, or both. */
export interface SlideOutput {
  /** The slide number from the tags; null for an output without a tag. */
  index: number | null;
  still?: string;
  video?: string;
}

const MANAGED_UPLOAD = /\/media\/uploads\/([0-9a-f-]{36})\.[a-z0-9]+(?:[?#].*)?$/i;

/** The media id a render output is stored under: /media/uploads/<id>.<ext> (Go managedmedia.Persist). */
export const renderOutputMediaId = (url: string): string => MANAGED_UPLOAD.exec((url || '').trim())?.[1] ?? '';

/** The slide tag on a render output's media metadata; null when it has none. */
export const readSlideTag = (metadata: unknown): SlideTag | null => {
  if (!isRecord(metadata)) return null;
  const index = Number(metadata.slide_index);
  const role: SlideTag['role'] | null =
    metadata.slide_role === 'still' ? 'still' : metadata.slide_role === 'video' ? 'video' : null;
  if (!Number.isInteger(index) || index < 1 || !role) return null;
  return { index, role };
};

/**
 * Groups a render that mixes stills and videos into one tile per slide, by the
 * slide index the backend tagged each output with — never by position: the
 * backend skips an output it could not store (Go renderVideo.go:93-99), so a
 * lost still would hand the next clip to the slide before it. An output without
 * a tag, or with a slide and role another output already filled, gets its own
 * tile after the numbered slides, in URL order.
 */
export const groupSlideOutputs = (urls: string[], tags: Record<string, SlideTag | undefined>): SlideOutput[] => {
  const bySlide = new Map<number, SlideOutput>();
  const untagged: SlideOutput[] = [];
  urls.forEach((url) => {
    const type = getFileType(url);
    if (type !== 'image' && type !== 'video') return;
    const tag = tags[url];
    if (!tag) {
      untagged.push(type === 'image' ? { index: null, still: url } : { index: null, video: url });
      return;
    }
    const slide = bySlide.get(tag.index) ?? { index: tag.index };
    // A second output claiming a filled slot keeps a tile of its own: never lose a file.
    if (tag.role === 'still' ? slide.still : slide.video) {
      untagged.push(tag.role === 'still' ? { index: null, still: url } : { index: null, video: url });
      return;
    }
    if (tag.role === 'still') slide.still = url;
    else slide.video = url;
    bySlide.set(tag.index, slide);
  });
  const numbered = Array.from(bySlide.values()).sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  return [...numbered, ...untagged];
};
